import { useMsal } from "@azure/msal-react";
import React, { useState, useEffect } from 'react';
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { models, IEmbedConfiguration } from "powerbi-client";
import { PowerBIEmbed } from 'powerbi-client-react';
import { IPowerBIEmbedParams } from "../types/IPowerBIEmbedParams";
import { IPowerBIAnalyticsConfig } from "../types/IPowerBIAnalyticsConfig";
import CarouselScrollButton from "../../repertoire/components/carouselScrollButton/CarouselScrollButton";
import ContributorsDataViewButton from "../../repertoire/works/components/contributorsDataViewButton/ContributorsDataViewButton";
import IMembersPortalComponentDataItem from "../../redux/types/IMembersPortalComponentDataItem";


export interface IAnalyticsPageProps {
    isCRMIntegrationEnabled: boolean;
    powerPortalURL: string;
    currentUserGuid: string;
    currentUserNameNumber: string;
    powerBIEmbedParams: IPowerBIEmbedParams;
    powerBIConfiguration: IPowerBIAnalyticsConfig[];
    statementsAnalyticsPageData: IMembersPortalComponentDataItem;
    getUserCRMValues: () => void;
    getPowerBiEmbed: (reportId: string, queryParameter: string) => IPowerBIEmbedParams;
    getPowerBIConfiguration: () => IPowerBIAnalyticsConfig;

}

const MOBILE_BREAKPOINT = 768;
const LANG_MAP: Record<string, string> = { "en-GB": "en-IE", "en-US": "en-IE", "fr": "fr-FR" };

const getLanguagePref = (): string => {
    const pref = JSON.parse(localStorage.getItem('userLanguagePreferences') || '"en-IE"');
    return LANG_MAP[pref] || pref;
};

const AnalyticsPage = (props: IAnalyticsPageProps & { dispatch?: any }) => {
    const { instance, accounts } = useMsal();
    const [language, setLanguage] = useState<string>(getLanguagePref);

    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= MOBILE_BREAKPOINT);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // The language dropdown calls window.location.reload() on change,
        // so same-tab changes are handled by the page reload.
        // This listener handles cross-tab language changes only.
        const onLanguageChange = () => setLanguage(getLanguagePref());
        window.addEventListener('storage', onLanguageChange);
        return () => window.removeEventListener('storage', onLanguageChange);
    }, []);

    React.useEffect(() => {
        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, [])

    React.useEffect(() => {
        if (!props.currentUserGuid) {
            props.getUserCRMValues();
        }
    }, [props.currentUserGuid])

    React.useEffect(() => {
        if (!props.powerBIConfiguration) {
            props.getPowerBIConfiguration();
        }
        else if(props.powerBIConfiguration && !props.powerBIEmbedParams){
            props.getPowerBiEmbed(props.powerBIConfiguration[0].reportId, props.powerBIConfiguration[0].queryParameter);
            
        }
    }, [props.powerBIConfiguration, props.powerBIEmbedParams])

    let embedConfiguration: IEmbedConfiguration;

    if(props.powerBIEmbedParams) {

        if(props.powerBIEmbedParams.embedToken && props.powerBIEmbedParams.embedReports && props.powerBIEmbedParams.embedReports.length > 0)
        {
            embedConfiguration = {
                accessToken: props.powerBIEmbedParams.embedToken.token,
                embedUrl: props.powerBIEmbedParams.embedReports[0].embedUrl,
                id: props.powerBIEmbedParams.embedReports[0].reportId,
                tokenType: models.TokenType.Embed,
                type: 'report',
                settings: {
                    layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Master,
                    localeSettings: {
                        language: language,
                        formatLocale: language
                    },
                    panes:{
                        filters: { visible: false },
                        pageNavigation: { visible: false }
                    }
                }
            };
        }
    }
    else{
        embedConfiguration = {
            embedUrl: undefined,
            id: undefined,
            tokenType: models.TokenType.Embed,
            type: 'report',
            accessToken: undefined,
            settings: {
                layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Master,
                panes:{
                    filters: { visible: false },
                    pageNavigation: { visible: false }
                }
            }
        };
    }        

    const onClick = (reportId:string) => {
        let reportDetail = props.powerBIConfiguration.find(item => item.reportId === reportId);
        props.getPowerBiEmbed(reportDetail.reportId, reportDetail.queryParameter);
    }

    const renderReportButtons = () => {

        if(props.powerBIConfiguration && props.powerBIEmbedParams){
            return props.powerBIConfiguration.map((item, index) => (
                <ContributorsDataViewButton
                    key={index}
                    text={props.statementsAnalyticsPageData.fields.find((v, i, a) => String(v.name) === item.reportName)?.data || item.reportName}
                    onClick={() => onClick(item.reportId)}
                    active={props.powerBIEmbedParams?.embedReports[0].reportId === item.reportId}
                />
            ));
        }        
    };    

    let powerbiReport;
    if(embedConfiguration){
        powerbiReport = (
            <PowerBIEmbed
                key={`${isMobile ? 'mobile' : 'desktop'}-${language}-${props.powerBIEmbedParams?.embedToken?.tokenId ?? ''}`}
                embedConfig={embedConfiguration}
            />
        );
    }

     let headerText = "";

    if (props.statementsAnalyticsPageData && props.statementsAnalyticsPageData.fields.length > 0) {
        const pageDescriptionField = props.statementsAnalyticsPageData.fields.find((v, i, a) => String(v.name) === "pageDescription");
        headerText = pageDescriptionField ? pageDescriptionField.data : "";
        }

    return (
        <div>            
            <div className={"powerbiReportContainer"} >
                {powerbiReport}
            </div>
        </div>
    );
}

export default AnalyticsPage;